/*
 * andCharlie size rules, schema version 1. No store data or example thresholds live here.
 * Pure API: validateSystem(system), validateProduct(system, product),
 * resolveBody(profile, system), applyPreference(result, preference, profile),
 * resolveFit(profile, system, preference?, product?). No DOM, storage, network or stock reads.
 *
 * Profile: { weightKg, shortBody: 'yes'|'no'|'unknown', lengthCm?, chestCm?, neckCm? }.
 * System: { key, method: 'body'|'measurement'|'none', revision, optionNames?:[strings],
 *   chart: { columns: [{key,label,unit?}],
 *     rows: [{key,label,optionValues:[exact Shopify strings],cells:{columnKey:string}}] },
 *   rules?: { schemaVersion:1, status:'approved'|'awaiting_data',
 *     inputBounds?: {profileNumberField:{min,max}},
 *     weightBands?: [{min,max,maxInclusive?,candidates:[row keys],preference?,requiredMeasurements?,
 *       shortBodyCandidates?:{yes:[row keys],no:[row keys]},
 *       shortBodyPreferences?:{yes?,no?,unknown?},
 *       measurementChecks?:[{field,min,max,outcome:'stop'|'equal'|'smaller'|'larger'}]}],
 *     shortBodyHints?: [{weight:{min,max},length:{min,max},tendency:'short'|'standard'}],
 *     requiredMeasurements?:[measurement fields],
 *     measurementRules?:[{when:{measurementField:{min,max}},candidates,preference?}]
 *   } }.
 * Rule ranges are [min, max); a weight band's explicit maxInclusive includes its
 * exact last endpoint (including a point band). Bounds must remain finite.
 * inputBounds instead include both endpoints. Every populated numeric input must be
 * finite and positive; physiological limits exist only when explicitly supplied.
 * Unknown shortBody asks length only for a matching approved weight/length hint.
 * Hints remain advisory bodyTendency; they never change the owner's shortBody answer.
 * Measurement checks request their fields, then only rank or stop the existing
 * weight-derived candidate set. Body length requirements/checks apply only when
 * the owner answered unknown; questions are ordered length, chest, then neck.
 * Two applicable checks with different outcomes fail closed. Absent rules never infer.
 * Explicit body candidate sets must be subsets of their weight band. An unknown
 * answer preserves their union in chart order, without selecting a preferred size.
 *
 * Preference: null means preserve common preference. Otherwise
 * {preference:'equal'|'smaller'|'larger',conditions?:[{shortBody,preference}],
 *   status?:'approved',schemaVersion?:1,key?,revision?}.
 * Conditions use the owner's answer (including unknown), not an inferred tendency.
 * Preferences cannot contain size keys or change baseCandidates.
 * Product: {optionName,optionValues:[actual Shopify names],availableByLabel?,id?}.
 * optionValues are exact matches against central row aliases; missing, ambiguous,
 * duplicate or unknown values stop fitting. A candidate not offered stops fitting
 * instead of promoting the remaining size. availableByLabel is deliberately ignored.
 *
 * Validators return {valid,errors:[{code,path}],...}. Fit results always contain
 * {status,reason,baseCandidates,rankedCandidates,preferredKey,requestedFields,
 *  systemKey,revision}; statuses: ready, needs_input, no_rules, invalid, no_match,
 * no_size. Candidate arrays contain keys, canonically ordered by chart rows in
 * baseCandidates. Row keys use letters, digits, underscores and hyphens, keeping
 * Liquid's delimited key-set checks unambiguous. Labels have no such restriction.
 * rankedCandidates changes only for an explicit preference.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) /** @type {any} */ (root).AndCharlieSizeEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
  const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const text = (value) => typeof value === 'string' && value.trim().length > 0;
  const finite = (value) => typeof value === 'number' && Number.isFinite(value);
  const measurements = ['lengthCm', 'chestCm', 'neckCm'];
  const numericFields = ['weightKg'].concat(measurements);
  const bodyAnswers = ['yes', 'no', 'unknown'];
  const preferences = ['equal', 'smaller', 'larger'];
  const inRange = (value, range) => value >= range.min && (value < range.max || (range.maxInclusive === true && value === range.max));
  const overlaps = (left, right) => (left.min < right.max || (right.maxInclusive === true && left.min === right.max)) &&
    (right.min < left.max || (left.maxInclusive === true && right.min === left.max));
  const unique = (values) => Array.from(new Set(values));
  const absent = (value) => value === undefined || value === null || value === '';

  // Normalize only when reading a submitted value; never rewrite an active IME input.
  function parseNumber(value) {
    if (finite(value)) return value;
    if (typeof value !== 'string') return NaN;
    const normalized = value.normalize('NFKC').trim().replace(/[、,]/g, '.');
    return /^(?:\d+(?:\.\d+)?|\.\d+)$/.test(normalized) ? Number(normalized) : NaN;
  }

  function error(errors, code, path) { errors.push({ code, path }); }
  function allowedKeys(value, keys, errors, path) {
    Object.keys(value).forEach((key) => {
      if (!keys.includes(key)) error(errors, 'unsupported_rule_field', path + '.' + key);
    });
  }
  function rangeValid(value, errors, path, inclusive) {
    if (!object(value) || !finite(value.min) || !finite(value.max) || value.min < 0 ||
      (inclusive ? value.max < value.min : value.max <= value.min)) {
      error(errors, 'invalid_range', path);
      return false;
    }
    return true;
  }
  function measurementList(value, errors, path) {
    if (value === undefined) return true;
    if (!Array.isArray(value) || value.some((field) => !measurements.includes(field)) || unique(value).length !== value.length) {
      error(errors, 'invalid_measurement_fields', path);
      return false;
    }
    return true;
  }
  function candidateList(value, rowKeys, errors, path) {
    if (!Array.isArray(value) || value.length < 1 || value.length > 2 ||
      value.some((key) => !rowKeys.has(key)) || unique(value).length !== value.length) {
      error(errors, 'invalid_candidates', path);
      return false;
    }
    return true;
  }

  function validateSystem(system) {
    const errors = [];
    if (!object(system)) return { valid: false, errors: [{ code: 'missing_system', path: 'system' }] };
    if (!text(system.key)) error(errors, 'missing_system_key', 'key');
    if (!['body', 'measurement', 'none'].includes(system.method)) error(errors, 'invalid_method', 'method');
    if (!text(system.revision)) error(errors, 'missing_revision', 'revision');
    if (system.optionNames !== undefined && (!Array.isArray(system.optionNames) || !system.optionNames.length ||
      system.optionNames.some((name) => !text(name)) || unique(system.optionNames).length !== system.optionNames.length)) error(errors, 'invalid_option_names', 'optionNames');
    const chart = system.chart;
    if (!object(chart) || !Array.isArray(chart.columns) || !Array.isArray(chart.rows)) {
      error(errors, 'invalid_chart', 'chart');
      return { valid: false, errors };
    }
    if (system.method !== 'none' && (!chart.rows.length || !chart.columns.length)) error(errors, 'empty_chart', 'chart');
    const columns = new Set();
    chart.columns.forEach((column, index) => {
      const path = 'chart.columns[' + index + ']';
      if (!object(column) || !text(column.key) || !text(column.label)) {
        error(errors, 'invalid_column', path);
        return;
      }
      if (columns.has(column.key)) error(errors, 'duplicate_column', path + '.key');
      if (column.unit !== undefined && typeof column.unit !== 'string') error(errors, 'invalid_unit', path + '.unit');
      columns.add(column.key);
    });
    const rowKeys = new Set();
    const labels = new Set();
    const optionMap = new Map();
    chart.rows.forEach((row, index) => {
      const path = 'chart.rows[' + index + ']';
      if (!object(row) || !text(row.key) || !text(row.label)) {
        error(errors, 'invalid_row', path);
        return;
      }
      if (rowKeys.has(row.key)) error(errors, 'duplicate_row_key', path + '.key');
      if (!/^[a-zA-Z0-9_-]+$/.test(row.key)) error(errors, 'invalid_row_key', path + '.key');
      if (labels.has(row.label)) error(errors, 'duplicate_row_label', path + '.label');
      rowKeys.add(row.key);
      labels.add(row.label);
      if (!Array.isArray(row.optionValues) || !row.optionValues.length || row.optionValues.some((value) => !text(value))) {
        error(errors, 'invalid_option_aliases', path + '.optionValues');
      } else {
        row.optionValues.forEach((value) => {
          if (optionMap.has(value)) error(errors, 'duplicate_option_alias', path + '.optionValues');
          optionMap.set(value, row.key);
        });
      }
      if (!object(row.cells)) error(errors, 'invalid_cells', path + '.cells');
      else {
        columns.forEach((key) => {
          if (!own(row.cells, key) || typeof row.cells[key] !== 'string') error(errors, 'missing_chart_cell', path + '.cells.' + key);
        });
        Object.keys(row.cells).forEach((key) => {
          if (!columns.has(key)) error(errors, 'unknown_chart_column', path + '.cells.' + key);
        });
      }
    });
    const rules = system.rules;
    if (rules === undefined || rules === null) return { valid: errors.length === 0, errors };
    if (!object(rules)) {
      error(errors, 'invalid_rules', 'rules');
      return { valid: false, errors };
    }
    allowedKeys(rules, ['schemaVersion', 'status', 'inputBounds', 'weightBands', 'shortBodyHints', 'requiredMeasurements', 'measurementRules'], errors, 'rules');
    if (rules.schemaVersion !== 1) error(errors, 'unsupported_rules_version', 'rules.schemaVersion');
    if (!['approved', 'awaiting_data'].includes(rules.status)) error(errors, 'invalid_rules_status', 'rules.status');
    if (rules.inputBounds !== undefined) {
      if (!object(rules.inputBounds)) error(errors, 'invalid_input_bounds', 'rules.inputBounds');
      else Object.keys(rules.inputBounds).forEach((field) => {
        if (!numericFields.includes(field)) error(errors, 'unknown_input_field', 'rules.inputBounds.' + field);
        rangeValid(rules.inputBounds[field], errors, 'rules.inputBounds.' + field, true);
      });
    }
    measurementList(rules.requiredMeasurements, errors, 'rules.requiredMeasurements');
    ['weightBands', 'shortBodyHints', 'measurementRules'].forEach((key) => {
      if (rules[key] !== undefined && !Array.isArray(rules[key])) error(errors, 'invalid_rule_list', 'rules.' + key);
    });
    const bands = Array.isArray(rules.weightBands) ? rules.weightBands : [];
    const validBands = [];
    bands.forEach((band, index) => {
      const path = 'rules.weightBands[' + index + ']';
      if (!object(band)) { error(errors, 'invalid_weight_band', path); return; }
      allowedKeys(band, ['min', 'max', 'maxInclusive', 'candidates', 'preference', 'requiredMeasurements', 'shortBodyPreferences', 'shortBodyCandidates', 'measurementChecks'], errors, path);
      if (band.maxInclusive !== undefined && typeof band.maxInclusive !== 'boolean') error(errors, 'invalid_max_inclusive', path);
      if (rangeValid(band, errors, path, band.maxInclusive === true)) validBands.push(band);
      candidateList(band.candidates, rowKeys, errors, path + '.candidates');
      if (band.shortBodyCandidates !== undefined) {
        if (!object(band.shortBodyCandidates)) error(errors, 'invalid_body_candidates', path);
        else {
          allowedKeys(band.shortBodyCandidates, ['yes', 'no'], errors, path + '.shortBodyCandidates');
          for (const answer of ['yes', 'no']) {
            const values = band.shortBodyCandidates[answer];
            if (candidateList(values, rowKeys, errors, path + '.shortBodyCandidates.' + answer) &&
                values.some(key => !Array.isArray(band.candidates) || !band.candidates.includes(key))) error(errors, 'body_candidates_outside_band', path);
          }
        }
      }
      if (band.preference !== undefined && !preferences.includes(band.preference)) error(errors, 'invalid_preference', path + '.preference');
      measurementList(band.requiredMeasurements, errors, path + '.requiredMeasurements');
      if (band.shortBodyPreferences !== undefined) {
        if (!object(band.shortBodyPreferences)) error(errors, 'invalid_body_preferences', path + '.shortBodyPreferences');
        else Object.keys(band.shortBodyPreferences).forEach((answer) => {
          if (!bodyAnswers.includes(answer) || !preferences.includes(band.shortBodyPreferences[answer])) error(errors, 'invalid_body_preference', path + '.shortBodyPreferences.' + answer);
        });
      }
      if (band.measurementChecks !== undefined) {
        if (!Array.isArray(band.measurementChecks)) error(errors, 'invalid_measurement_checks', path + '.measurementChecks');
        else band.measurementChecks.forEach((check, checkIndex) => {
          const checkPath = path + '.measurementChecks[' + checkIndex + ']';
          if (!object(check)) { error(errors, 'invalid_measurement_check', checkPath); return; }
          allowedKeys(check, ['field', 'min', 'max', 'outcome'], errors, checkPath);
          if (!measurements.includes(check.field)) error(errors, 'unknown_measurement_field', checkPath + '.field');
          rangeValid(check, errors, checkPath);
          if (!['stop'].concat(preferences).includes(check.outcome)) error(errors, 'invalid_check_outcome', checkPath + '.outcome');
        });
      }
    });
    validBands.forEach((band, index) => {
      if (validBands.slice(index + 1).some((other) => overlaps(band, other))) error(errors, 'overlapping_weight_bands', 'rules.weightBands');
    });
    const hints = Array.isArray(rules.shortBodyHints) ? rules.shortBodyHints : [];
    const validHints = [];
    hints.forEach((hint, index) => {
      const path = 'rules.shortBodyHints[' + index + ']';
      if (!object(hint)) { error(errors, 'invalid_body_hint', path); return; }
      allowedKeys(hint, ['weight', 'length', 'tendency'], errors, path);
      const weightValid = rangeValid(hint.weight, errors, path + '.weight');
      const lengthValid = rangeValid(hint.length, errors, path + '.length');
      if (weightValid && lengthValid) validHints.push(hint);
      if (!['short', 'standard'].includes(hint.tendency)) error(errors, 'invalid_body_tendency', path + '.tendency');
    });
    validHints.forEach((hint, index) => {
      if (validHints.slice(index + 1).some((other) => overlaps(hint.weight, other.weight) && overlaps(hint.length, other.length))) error(errors, 'overlapping_body_hints', 'rules.shortBodyHints');
    });
    const measurementRules = Array.isArray(rules.measurementRules) ? rules.measurementRules : [];
    const validMeasurementRules = [];
    measurementRules.forEach((rule, index) => {
      const path = 'rules.measurementRules[' + index + ']';
      if (!object(rule)) { error(errors, 'invalid_measurement_rule', path); return; }
      allowedKeys(rule, ['when', 'candidates', 'preference'], errors, path);
      candidateList(rule.candidates, rowKeys, errors, path + '.candidates');
      if (rule.preference !== undefined && !preferences.includes(rule.preference)) error(errors, 'invalid_preference', path + '.preference');
      if (!object(rule.when) || !Object.keys(rule.when).length) error(errors, 'missing_measurement_condition', path + '.when');
      else {
        let valid = true;
        Object.keys(rule.when).forEach((field) => {
          if (!measurements.includes(field) || !Array.isArray(rules.requiredMeasurements) || !rules.requiredMeasurements.includes(field)) {
            error(errors, 'undeclared_measurement_condition', path + '.when.' + field);
            valid = false;
          }
          if (!rangeValid(rule.when[field], errors, path + '.when.' + field)) valid = false;
        });
        if (valid) validMeasurementRules.push(rule);
      }
    });
    validMeasurementRules.forEach((rule, index) => {
      const collision = validMeasurementRules.slice(index + 1).some((other) => {
        const common = Object.keys(rule.when).filter((field) => own(other.when, field));
        return common.every((field) => overlaps(rule.when[field], other.when[field]));
      });
      if (collision) error(errors, 'overlapping_measurement_rules', 'rules.measurementRules');
    });
    if (system.method === 'body' && (measurementRules.length || (rules.requiredMeasurements || []).length)) error(errors, 'wrong_method_rules', 'rules');
    if (system.method === 'measurement' && (bands.length || hints.length)) error(errors, 'wrong_method_rules', 'rules');
    if (system.method === 'none' && (bands.length || hints.length || measurementRules.length)) error(errors, 'wrong_method_rules', 'rules');
    if (rules.status === 'approved' && ((system.method === 'body' && !bands.length) ||
      (system.method === 'measurement' && (!measurementRules.length || !(rules.requiredMeasurements || []).length)))) error(errors, 'missing_approved_rules', 'rules');
    return { valid: errors.length === 0, errors };
  }

  function validateProduct(system, product) {
    const validation = validateSystem(system);
    if (!validation.valid) return validation;
    if (system.method === 'none') return { valid: true, errors: [], offeredKeys: [], labelByKey: {} };
    const errors = [];
    if (!object(product) || !text(product.optionName) || !Array.isArray(product.optionValues) || !product.optionValues.length) {
      return { valid: false, errors: [{ code: 'missing_size_option', path: 'product.optionValues' }] };
    }
    if (system.optionNames && !system.optionNames.includes(product.optionName)) error(errors, 'unmapped_option_name', 'product.optionName');
    const map = new Map();
    system.chart.rows.forEach((row) => row.optionValues.forEach((label) => map.set(label, row.key)));
    const offered = new Set();
    const labelByKey = Object.create(null);
    product.optionValues.forEach((label, index) => {
      if (!text(label) || !map.has(label)) error(errors, 'unmapped_size_option', 'product.optionValues[' + index + ']');
      else {
        const key = map.get(label);
        if (offered.has(key)) error(errors, 'ambiguous_size_option', 'product.optionValues[' + index + ']');
        offered.add(key);
        labelByKey[key] = label;
      }
    });
    return { valid: errors.length === 0, errors,
      offeredKeys: system.chart.rows.filter((row) => offered.has(row.key)).map((row) => row.key), labelByKey };
  }

  function result(system, status, reason, additions) {
    return Object.assign({ status, reason: reason || null, baseCandidates: [], rankedCandidates: [],
      preferredKey: null, requestedFields: [], systemKey: object(system) ? system.key || null : null,
      revision: object(system) ? system.revision || null : null }, additions || {});
  }
  function stopped(previous, status, reason, additions) {
    return Object.assign({}, previous, { status, reason, rankedCandidates: [], preferredKey: null, requestedFields: [] }, additions || {});
  }
  function checkProfile(profile, rules) {
    if (!object(profile)) return { code: 'invalid_profile' };
    for (const field of numericFields) {
      if (absent(profile[field])) continue;
      if (!finite(profile[field]) || profile[field] <= 0) return { code: 'invalid_input', field };
      const bounds = rules.inputBounds && rules.inputBounds[field];
      if (bounds && (profile[field] < bounds.min || profile[field] > bounds.max)) return { code: 'input_out_of_bounds', field };
    }
    if (!absent(profile.shortBody) && !bodyAnswers.includes(profile.shortBody)) return { code: 'invalid_short_body', field: 'shortBody' };
    return null;
  }
  function rank(base, preference) {
    const preferredKey = base.length === 1 ? base[0] : preference === 'smaller' ? base[0] : preference === 'larger' ? base[base.length - 1] : null;
    return { preferredKey, rankedCandidates: preferredKey ? [preferredKey].concat(base.filter((key) => key !== preferredKey)) : base.slice() };
  }
  function canonical(system, candidates) {
    return system.chart.rows.filter((row) => candidates.includes(row.key)).map((row) => row.key);
  }
  function initial(system) {
    const validation = validateSystem(system);
    if (!validation.valid) return result(system, 'invalid', 'invalid_system', { errors: validation.errors });
    if (system.method === 'none') return result(system, 'no_size', 'no_size_selection');
    if (!system.rules || system.rules.status !== 'approved') return result(system, 'no_rules', 'rules_not_approved');
    return null;
  }
  function inputFailure(system, failure) {
    return result(system, 'invalid', failure.code, { invalidField: failure.field || null });
  }

  function resolveBody(profile, system) {
    const pending = initial(system);
    if (pending) return pending;
    if (system.method !== 'body') return result(system, 'invalid', 'wrong_selection_method');
    const rules = system.rules;
    const failure = checkProfile(profile, rules);
    if (failure) return inputFailure(system, failure);
    if (absent(profile.weightKg)) return result(system, 'needs_input', 'weight_required', { requestedFields: ['weightKg'] });
    if (absent(profile.shortBody)) return result(system, 'needs_input', 'short_body_required', { requestedFields: ['shortBody'] });
    const matches = rules.weightBands.filter((band) => inRange(profile.weightKg, band));
    if (matches.length !== 1) return result(system, matches.length ? 'invalid' : 'no_match', matches.length ? 'ambiguous_weight_band' : 'weight_not_covered');
    const band = matches[0];
    const hints = profile.shortBody === 'unknown' ? (rules.shortBodyHints || []).filter((hint) => inRange(profile.weightKg, hint.weight)) : [];
    const declared = new Set((band.requiredMeasurements || [])
      .concat((band.measurementChecks || []).map((check) => check.field))
      .concat(hints.length ? ['lengthCm'] : []));
    const required = measurements.filter((field) => declared.has(field) &&
      (field !== 'lengthCm' || profile.shortBody === 'unknown'));
    const missing = required.filter((field) => absent(profile[field]));
    if (missing.length) return result(system, 'needs_input', 'measurements_required', { requestedFields: missing });
    let bodyTendency = profile.shortBody === 'yes' ? 'short' : profile.shortBody === 'no' ? 'standard' : 'unknown';
    const matchedHints = hints.filter((hint) => inRange(profile.lengthCm, hint.length));
    if (matchedHints.length > 1) return result(system, 'invalid', 'ambiguous_body_hint');
    if (matchedHints.length === 1) bodyTendency = matchedHints[0].tendency;
    const preferenceAnswer = bodyTendency === 'short' ? 'yes' : bodyTendency === 'standard' ? 'no' : 'unknown';
    let preference = band.preference || 'equal';
    if (band.shortBodyPreferences && own(band.shortBodyPreferences, preferenceAnswer)) preference = band.shortBodyPreferences[preferenceAnswer];
    const checks = (band.measurementChecks || []).filter((check) =>
      (check.field !== 'lengthCm' || profile.shortBody === 'unknown') &&
      !absent(profile[check.field]) && inRange(profile[check.field], check));
    const outcomes = unique(checks.map((check) => check.outcome));
    if (outcomes.includes('stop')) return result(system, 'no_match', 'measurement_check_stopped');
    if (outcomes.length > 1) return result(system, 'invalid', 'conflicting_measurement_checks');
    if (outcomes.length === 1) preference = outcomes[0];
    const unknown = profile.shortBody === 'unknown';
    const candidates = unknown && band.shortBodyCandidates
      ? unique(band.shortBodyCandidates.yes.concat(band.shortBodyCandidates.no))
      : band.shortBodyCandidates?.[preferenceAnswer] || band.candidates;
    const baseCandidates = canonical(system, candidates);
    return result(system, 'ready', null, Object.assign({ baseCandidates, bodyTendency,
      shortBodySource: matchedHints.length ? 'approved_hint' : unknown ? 'unknown' : 'owner' }, rank(baseCandidates, unknown ? 'equal' : preference)));
  }

  function applyPreference(previous, preference, profile) {
    if (!object(previous)) return result(null, 'invalid', 'invalid_base_result');
    if (previous.status !== 'ready') return previous;
    if (!Array.isArray(previous.baseCandidates) || previous.baseCandidates.length < 1 || previous.baseCandidates.length > 2 ||
      previous.baseCandidates.some((key) => !text(key)) || unique(previous.baseCandidates).length !== previous.baseCandidates.length ||
      !Array.isArray(previous.rankedCandidates)) return stopped(previous, 'invalid', 'invalid_base_result');
    if (preference === undefined || preference === null) return Object.assign({}, previous, {
      baseCandidates: previous.baseCandidates.slice(), rankedCandidates: previous.rankedCandidates.slice() });
    if (!object(preference)) return stopped(previous, 'invalid', 'invalid_product_preference');
    const errors = [];
    allowedKeys(preference, ['preference', 'conditions', 'status', 'schemaVersion', 'key', 'revision'], errors, 'preference');
    if (!preferences.includes(preference.preference)) error(errors, 'invalid_preference', 'preference.preference');
    if (preference.schemaVersion !== undefined && preference.schemaVersion !== 1) error(errors, 'unsupported_rules_version', 'preference.schemaVersion');
    if (preference.status !== undefined && preference.status !== 'approved') error(errors, 'unapproved_preference', 'preference.status');
    const conditions = preference.conditions === undefined ? [] : preference.conditions;
    if (!Array.isArray(conditions)) error(errors, 'invalid_preference_conditions', 'preference.conditions');
    else {
      const seen = new Set();
      conditions.forEach((condition, index) => {
        const path = 'preference.conditions[' + index + ']';
        if (!object(condition)) { error(errors, 'invalid_preference_condition', path); return; }
        allowedKeys(condition, ['shortBody', 'preference'], errors, path);
        if (!bodyAnswers.includes(condition.shortBody) || !preferences.includes(condition.preference)) error(errors, 'invalid_preference_condition', path);
        if (seen.has(condition.shortBody)) error(errors, 'ambiguous_preference_condition', path);
        seen.add(condition.shortBody);
      });
    }
    if (errors.length) return stopped(previous, 'invalid', 'invalid_product_preference', { errors });
    if (!object(profile) || !bodyAnswers.includes(profile.shortBody)) {
      if (conditions.length) return stopped(previous, 'invalid', 'missing_preference_input');
    }
    const match = conditions.find((condition) => profile && condition.shortBody === profile.shortBody);
    const selected = profile?.shortBody === 'unknown' ? 'equal' : match ? match.preference : preference.preference;
    const baseCandidates = previous.baseCandidates.slice();
    return Object.assign({}, previous, { baseCandidates }, rank(baseCandidates, selected));
  }

  function resolveMeasurement(profile, system) {
    const pending = initial(system);
    if (pending) return pending;
    const failure = checkProfile(profile, system.rules);
    if (failure) return inputFailure(system, failure);
    const missing = system.rules.requiredMeasurements.filter((field) => absent(profile[field]));
    if (missing.length) return result(system, 'needs_input', 'measurements_required', { requestedFields: missing });
    const matches = system.rules.measurementRules.filter((rule) => Object.keys(rule.when).every((field) => inRange(profile[field], rule.when[field])));
    if (matches.length !== 1) return result(system, matches.length ? 'invalid' : 'no_match', matches.length ? 'ambiguous_measurement_rule' : 'measurements_not_covered');
    const baseCandidates = canonical(system, matches[0].candidates);
    return result(system, 'ready', null, Object.assign({ baseCandidates }, rank(baseCandidates, matches[0].preference || 'equal')));
  }

  function resolveFit(profile, system, preference, product) {
    const pending = initial(system);
    if (pending) return pending;
    let productValidation = null;
    if (product !== undefined && product !== null) {
      productValidation = validateProduct(system, product);
      if (!productValidation.valid) return result(system, 'invalid', 'product_size_mismatch', { errors: productValidation.errors });
    }
    let resolved = system.method === 'body' ? resolveBody(profile, system) : resolveMeasurement(profile, system);
    if (resolved.status !== 'ready') return resolved;
    if (productValidation && resolved.baseCandidates.some((key) => !productValidation.offeredKeys.includes(key))) return stopped(resolved, 'invalid', 'candidate_not_offered');
    if (system.method === 'measurement' && preference !== undefined && preference !== null) return stopped(resolved, 'invalid', 'body_preference_on_measurement_system');
    if (system.method === 'body') resolved = applyPreference(resolved, preference, profile);
    return resolved;
  }

  return Object.freeze({ version: '2026-10-13-unknown-union', parseNumber, validateSystem, validateProduct, resolveBody, applyPreference, resolveFit });
});
